/**
 * La venta que pasa el limite de credito del cliente.
 *
 * El limite AVISA, no bloquea: es lo que dice la ficha del cliente. Pero
 * `emitir_venta` rechaza la venta con un texto que termina en «Confirma la
 * venta con "forzar" si igual queres hacerla», y la pantalla lo mostraba en un
 * toast rojo, con los numeros sin formato («17500.00 ARS») y sin ningun boton
 * para confirmar: la unica salida era ir a sacarle el limite al cliente.
 *
 * Aca se reconoce ESE rechazo y se lee lo que dice, para que la pantalla pueda
 * preguntar «¿Vender igual?» con los numeros bien escritos y reintentar con
 * `forzar: true`.
 */
import { convertir, formatear, type Moneda } from "@tora/shared";

import { mensajeDeError } from "@/lib/api";
import type { FichaCliente } from "@/lib/tipos";
import { aNumero } from "@/lib/tipos";

import { creditoDisponible, monedaAnclaje } from "@/components/clientes/logica";

export interface CreditoExcedido {
  /** La venta, en la moneda del limite. */
  venta: number;
  /** Lo que le quedaba disponible. Negativo si ya estaba pasado. */
  queda: number;
  moneda: Moneda;
  /**
   * En un pago parcial, lo que queda debiendo, en la moneda del limite. La
   * base compara el TOTAL de la venta, pero lo que va a la cuenta es esto: el
   * dialogo decia «esta venta es de $ 14.000» por una deuda de $ 13.000.
   */
  deuda?: number;
}

/** El texto de `emitir_venta` (0037). Si cambia alla, cambia aca. */
const RECHAZO =
  /La venta de (-?\d+(?:\.\d+)?) (ARS|USD) excede el credito disponible del cliente \(queda (-?\d+(?:\.\d+)?) (?:ARS|USD)\)/;

export function leerCreditoExcedido(error: unknown): CreditoExcedido | null {
  const m = RECHAZO.exec(mensajeDeError(error));
  if (!m) return null;
  return { venta: Number(m[1]), queda: Number(m[3]), moneda: m[2] as Moneda };
}

/** Lo que se le pregunta a quien vende, con los numeros escritos como plata. */
export function textoCreditoExcedido(c: CreditoExcedido, cliente: string | null): string {
  const quien = cliente?.trim() || "El cliente";
  const venta = formatear(c.venta, c.moneda);
  if (c.deuda !== undefined) {
    const deuda = formatear(c.deuda, c.moneda);
    return c.queda > 0.009
      ? `${quien} tiene ${formatear(c.queda, c.moneda)} de crédito disponible y esta venta le deja una deuda de ${deuda}.`
      : c.queda < -0.009
        ? `${quien} ya pasó su límite de crédito por ${formatear(-c.queda, c.moneda)}, y esta venta le suma una deuda de ${deuda}.`
        : `${quien} ya usó todo su límite de crédito, y esta venta le deja una deuda de ${deuda}.`;
  }
  return c.queda > 0.009
    ? `${quien} tiene ${formatear(c.queda, c.moneda)} de crédito disponible y esta venta es de ${venta}.`
    : c.queda < -0.009
      ? `${quien} ya pasó su límite de crédito por ${formatear(-c.queda, c.moneda)}, y esta venta suma ${venta}.`
      : `${quien} ya usó todo su límite de crédito, y esta venta suma ${venta}.`;
}

/**
 * Si la deuda que deja un pago parcial entra en el credito que le queda.
 *
 * `emitir_venta` (0037) mira el limite ANTES de que entren los pagos y compara
 * el TOTAL de la venta: una venta de $ 14.000 con $ 13.000 en efectivo, a un
 * cliente con $ 10.000 disponibles, rebotaba por «excede el credito» aunque la
 * deuda fuera de $ 1.000. Con esto la pantalla manda `forzar` cuando SABE que
 * la deuda entra, con la misma cuenta que `credito_disponible()`. Si no lo
 * sabe (sin la ficha, sin cotizacion, limite en cero) no fuerza nada: decide
 * la base, como antes.
 */
export function deudaEntraEnElCredito(p: {
  deuda: number;
  monedaVenta: Moneda;
  ficha: FichaCliente | null | undefined;
  cotizacion: number;
}): boolean {
  const { deuda, monedaVenta, ficha, cotizacion } = p;
  if (!ficha) return false;
  const credito = creditoDisponible({
    limite: ficha.cliente.limite_credito,
    limiteMoneda: ficha.cliente.limite_moneda,
    saldo: aNumero(ficha.saldo?.saldo ?? 0),
    monedaSaldo: ficha.saldo?.moneda_anclaje ?? monedaAnclaje(ficha.cliente),
    cotizacion,
  });
  if (!credito) return false;
  if (credito.moneda !== monedaVenta && cotizacion <= 0) return false;
  const enLimite = convertir(deuda, monedaVenta, credito.moneda, cotizacion);
  return enLimite <= credito.disponible + 0.009;
}
